import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PaymentReminder from './../../paymentReminder/PaymentReminder';
import Notification, {
	NOTIFICATION_APPEARANCE_WIDE,
	NOTIFICATION_TYPE_ERROR,
	NOTIFICATION_TYPE_SUCCESS,
	NOTIFICATION_TYPE_INFORMATION,
} from '../../../common/notification/Notification';
import {
	NOTIFICATION_TIER_CHANGED,
	NOTIFICATION_TIER_CHANGED_QUEUED,
	NOTIFICATION_PAYMENT_CHANGED_SUCCESS,
	NOTIFICATION_PAYMENT_CHANGED_ERROR,
	PRICE_INCREASE,
} from '../../../../constants/Copy';
import {
	CHANGE_TIER_NOTIFICATION,
	CHANGE_TIER_QUEUED_NOTIFICATION,
	CHANGED_PAYMENT_NOTIFICATION_SUCCESS,
	CHANGED_PAYMENT_NOTIFICATION_ERROR,
} from '../../../../constants/Notifications';
import { hideNotification } from '../../../../actions/notificationActions';
import { analyticsPush } from '../../../../actions/applicationActions';
import { EVT_CHANGE_TIER_NOTIFICATION_HIDE } from '../../../../constants/EventTypes';
import { PAYMENT_ORIGIN } from '../../../../constants/Payment';
import { setCustomVariable } from '../../../../helpers/usabilla';
import { isNotificationActive } from '../../../../helpers/state';
import { fetchSubscriptionTier } from '../../../../actions/profileActions';
import { initiateTrackStructEvent } from '../../../analytics/Snowplow';
import {
	EVT_ACCOUNT,
	EVT_ELEMENT_INTERACTION,
	EVT_NOTIFICATION,
	EVT_CONFIRM_CLOSE,
	EVT_NOTIFICATION_PAYMENT_CLOSE,
	EVT_PAYMENT_CHANGE_NOTIFICATION,
} from '../../../../constants/SnowplowEvents';
import { OperationNames } from '../../../../constants/OtelSpans';
import { OperationTelemetry } from '@rtl_nl/rtl-otel-js-web';

const notificationTelemetry = new OperationTelemetry(OperationNames.NOTIFICATION);

function Notifications({ effectiveDate, openPayments, showPriceIncrease }) {
	const dispatch = useDispatch();
	const showQueuedTierChangedNotification = useSelector((state) =>
		isNotificationActive(state, CHANGE_TIER_QUEUED_NOTIFICATION)
	);
	const showTierChangedNotification = useSelector((state) =>
		isNotificationActive(state, CHANGE_TIER_NOTIFICATION)
	);
	const showPaymentChangedNotificationSuccess = useSelector((state) =>
		isNotificationActive(state, CHANGED_PAYMENT_NOTIFICATION_SUCCESS)
	);
	const showPaymentChangedNotificationError = useSelector((state) =>
		isNotificationActive(state, CHANGED_PAYMENT_NOTIFICATION_ERROR)
	);

	useEffect(() => {
		if (showTierChangedNotification) {
			setCustomVariable('switchedTier');
		}
	}, [showTierChangedNotification]);

	useEffect(() => {
		if (showPaymentChangedNotificationSuccess) {
			setCustomVariable('changePaymentDetails');
		}
	}, [showPaymentChangedNotificationSuccess]);

	function hideChangeTierNotification() {
		dispatch(
			analyticsPush({ label: NOTIFICATION_TIER_CHANGED.TITLE }, EVT_CHANGE_TIER_NOTIFICATION_HIDE)
		);
		dispatch(hideNotification(CHANGE_TIER_NOTIFICATION));
		dispatch(hideNotification(CHANGE_TIER_QUEUED_NOTIFICATION));
	}

	function hideChangedPaymentNotificationSuccess() {
		dispatch(hideNotification(CHANGED_PAYMENT_NOTIFICATION_SUCCESS));
	}

	function hideChangedPaymentNotificationError() {
		dispatch(hideNotification(CHANGED_PAYMENT_NOTIFICATION_ERROR));
	}

	useEffect(() => {
		if (showTierChangedNotification || showQueuedTierChangedNotification) {
			dispatch(fetchSubscriptionTier(notificationTelemetry));
		}
	}, [dispatch, showTierChangedNotification, showQueuedTierChangedNotification]);

	return (
		<div className="account account-page-bedrock-notifications">
			<PaymentReminder
				appearance={NOTIFICATION_APPEARANCE_WIDE}
				origin={PAYMENT_ORIGIN.SUBSCRIPTION_MANAGEMENT}
				openPayments={openPayments}
			/>
			<Notification
				description={PRICE_INCREASE}
				type={NOTIFICATION_TYPE_INFORMATION}
				visible={showPriceIncrease}
				className="notification--bottom-offset"
			/>
			<Notification
				title={NOTIFICATION_TIER_CHANGED.TITLE}
				description={NOTIFICATION_TIER_CHANGED.DESCRIPTION}
				type={NOTIFICATION_TYPE_SUCCESS}
				visible={showTierChangedNotification}
				className="notification--bottom-offset"
				button={{
					onClick: hideChangeTierNotification,
					text: NOTIFICATION_TIER_CHANGED.BUTTON,
				}}
			/>
			<Notification
				title={NOTIFICATION_TIER_CHANGED_QUEUED.TITLE}
				description={
					effectiveDate &&
					NOTIFICATION_TIER_CHANGED_QUEUED.DESCRIPTION.replace('{effective_date}', effectiveDate)
				}
				type={NOTIFICATION_TYPE_SUCCESS}
				visible={showQueuedTierChangedNotification}
				className="notification--bottom-offset"
				button={{
					onClick: hideChangeTierNotification,
					text: NOTIFICATION_TIER_CHANGED_QUEUED.BUTTON,
				}}
			/>
			<Notification
				title={NOTIFICATION_PAYMENT_CHANGED_SUCCESS.TITLE}
				description={NOTIFICATION_PAYMENT_CHANGED_SUCCESS.DESCRIPTION}
				type={NOTIFICATION_TYPE_SUCCESS}
				visible={showPaymentChangedNotificationSuccess}
				className="notification--bottom-offset"
				button={{
					onClick: () => {
						initiateTrackStructEvent({
							category: EVT_ACCOUNT,
							action: EVT_NOTIFICATION_PAYMENT_CLOSE,
							label: EVT_CONFIRM_CLOSE,

							rtlElementContext: {
								elementInteraction: EVT_ELEMENT_INTERACTION,
								elementGroup: EVT_PAYMENT_CHANGE_NOTIFICATION,
								elementType: EVT_NOTIFICATION,
								elementName: EVT_PAYMENT_CHANGE_NOTIFICATION,
								elementLabel: NOTIFICATION_PAYMENT_CHANGED_SUCCESS.BUTTON,
							},
						});
						hideChangedPaymentNotificationSuccess();
					},
					text: NOTIFICATION_PAYMENT_CHANGED_SUCCESS.BUTTON,
				}}
			/>
			<Notification
				title={NOTIFICATION_PAYMENT_CHANGED_ERROR.TITLE}
				description={NOTIFICATION_PAYMENT_CHANGED_ERROR.DESCRIPTION}
				type={NOTIFICATION_TYPE_ERROR}
				visible={showPaymentChangedNotificationError}
				className="notification--bottom-offset"
				button={{
					onClick: hideChangedPaymentNotificationError,
					text: NOTIFICATION_PAYMENT_CHANGED_ERROR.BUTTON,
				}}
			/>
		</div>
	);
}

export default Notifications;
